import { ChatMessage } from '@/app/chat/[avatarId]/server/db/chat';
import { extractS3KeyFromImageUri, extractPublicId } from '../util';
import { createCloudinaryImageSync } from '@/app/lib/cloudinary/cloudinary-image-utils';

/**
 * Enum for tracking image loading status
 */
export enum LiveImageStatus {
  NOT_LOADED = 'not_loaded',
  LOADING = 'loading',
  LOADED = 'loaded',
  FAILED = 'failed'
}

/**
 * Callback type for image status changes
 * imageData can be either a Cloudinary image object or HTMLImageElement for backward compatibility
 */
export type ImageStatusChangeCallback = (messageId: string, status: LiveImageStatus, imageData?: any) => void;

/**
 * Interface for image data with status (matches globalChatState pattern)
 */
export interface ImageWithStatus {
  imageUri: string | null; // S3 key with extension. Easy to extract public id for Cloudinary.
  imageData: any | null; // Cloudinary image object
  imageRawData: HTMLImageElement | null; // Raw image data, not often used, for backward compatibility.
  loadingStatus: LiveImageStatus;
}

/**
 * LiveMessage extends ChatMessage with additional real-time state fields
 * This is the single source of truth for message data in the chat module
 * Must use construction functions below, don't create directly
 */
export interface LiveMessage extends ChatMessage {
  /** Whether we are actively writing/streaming data into this message */
  isStreaming: boolean;
  
  // Use ImageWithStatus pattern to match globalChatState
  imageWithStatus: ImageWithStatus;
}

/**
 * Global registry for image status change callbacks
 */
const imageStatusCallbacks = new Map<string, ImageStatusChangeCallback>();

/**
 * Register a callback for image status changes
 * @param callbackId Unique identifier for this callback
 * @param callback Function to call when image status changes
 */
export function registerImageStatusCallback(callbackId: string, callback: ImageStatusChangeCallback) {
  imageStatusCallbacks.set(callbackId, callback);
}

/**
 * Unregister an image status change callback
 * @param callbackId Unique identifier for the callback to remove
 */
export function unregisterImageStatusCallback(callbackId: string) {
  imageStatusCallbacks.delete(callbackId);
}

/**
 * Notify all registered callbacks of an image status change
 * @param messageId The message ID that changed
 * @param status The new status
 * @param imageData The image data if loaded
 */
function notifyImageStatusChange(messageId: string, status: LiveImageStatus, imageData?: any) {
  imageStatusCallbacks.forEach(callback => {
    try {
      callback(messageId, status, imageData);
    } catch (error) {
      console.error('Error in image status callback:', error);
    }
  });
}

/**
 * Async function to handle Cloudinary image loading for a LiveMessage
 * @param message The LiveMessage to load image for
 * @param s3Key The S3 key to load
 */
async function loadCloudinaryImageForMessage(message: LiveMessage, s3Key: string) {
  try {
    console.log(`Starting async Cloudinary image loading for message ${message.id} with S3 key: ${s3Key}`);
    
    // Create Cloudinary image object
    const publicId = extractPublicId(s3Key);
    const cloudinaryImage = createCloudinaryImageSync(publicId);
    
    if (!cloudinaryImage) {
      throw new Error('Failed to create Cloudinary image');
    }
    
    // Only update if this is still the current S3 key
    if (message.imageWithStatus.imageUri === s3Key) {
      message.imageWithStatus.imageData = cloudinaryImage;
      message.imageWithStatus.loadingStatus = LiveImageStatus.LOADED;
      
      // Notify all listeners that image is loaded
    notifyImageStatusChange(message.id, LiveImageStatus.LOADED, cloudinaryImage);
    }
    
  } catch (error) {
    console.error(`Failed to load Cloudinary image for message ${message.id}:`, error);
    if (message.imageWithStatus.imageUri === s3Key) {
      message.imageWithStatus.loadingStatus = LiveImageStatus.FAILED;
      
      // Notify all listeners that image failed
      notifyImageStatusChange(message.id, LiveImageStatus.FAILED);
    }
  }
}

/**
 * Convert a ChatMessage from the database to a LiveMessage with default live state
 * @param chatMessage The database ChatMessage to convert
 * @returns LiveMessage with default streaming and image state
 */
export function constructLiveMessageFromChatMessage(chatMessage: ChatMessage): LiveMessage {
  return constructLiveMessage({
    id: chatMessage.id,
    content: chatMessage.content,
    role: chatMessage.role,
    sender_id: chatMessage.sender_id,
    sender_name: chatMessage.sender_name,
    created_at: chatMessage.created_at,
    imageUrl: chatMessage.imageUrl || '',
  });
}

export function constructLiveMessage({
  id,
  content,
  role,
  sender_id,
  sender_name,
  created_at,
  imageUrl,
  isStreaming
}: {
  id: string;
  content: string;
  role: 'user' | 'assistant';  // Required - fundamental to message type
  sender_id: string;           // Required - needed for routing/identity
  sender_name : string;         // Required - can default based on role
  created_at?: string;         // Optional - can default to current date
  imageUrl?: string;          // Optional - for image messages
  isStreaming?: boolean;      // Optional - for streaming messages
}): LiveMessage {
  let processedImageUrl = imageUrl || '';
  let initialImageStatus = LiveImageStatus.NOT_LOADED;

  // If message has an imageUrl, process it immediately (but non-blocking)
  if (imageUrl && imageUrl.trim()) {
    // Extract S3 key and overwrite imageUrl with the processed S3 key
    const s3Key = extractS3KeyFromImageUri(imageUrl);
    
    if (s3Key) {
      // Overwrite imageUrl with the processed S3 key
      processedImageUrl = s3Key;
      initialImageStatus = LiveImageStatus.LOADING;
    } else {
      // If we can't extract an S3 key, check if it's already a presigned URL
      if (imageUrl.startsWith('https://') || imageUrl.startsWith('http://')) {
        // It's already a presigned URL, keep as-is but mark as loading
        processedImageUrl = imageUrl;
        initialImageStatus = LiveImageStatus.LOADING;
      } else {
        // Can't process this imageUrl
        initialImageStatus = LiveImageStatus.FAILED;
      }
    }
  }

  // Create ImageWithStatus object
  const imageWithStatus: ImageWithStatus = {
    imageUri: processedImageUrl,
    imageData: null,
    imageRawData: null,
    loadingStatus: initialImageStatus
  };

  // Create and return the message immediately (non-blocking)
  const message: LiveMessage = {
    id: id,
    content: content,
    role,
    sender_id,
    sender_name: sender_name || (role === 'user' ? 'User' : 'Assistant'),
    created_at: created_at || new Date().toISOString(),
    imageUrl: processedImageUrl, // Use the processed imageUrl (S3 key or presigned URL)
    imageWithStatus: imageWithStatus,
    isStreaming: isStreaming || false,
  };

  // Start async image loading if needed (non-blocking)
  if (initialImageStatus === LiveImageStatus.LOADING && processedImageUrl) {
    if (processedImageUrl.startsWith('https://') || processedImageUrl.startsWith('http://')) {
      // It's already a presigned URL, load directly (fallback for old URLs)
      const img = new Image();
      img.onload = () => {
        message.imageWithStatus.imageRawData = img;
        message.imageWithStatus.loadingStatus = LiveImageStatus.LOADED;
        // For presigned URLs, we pass the HTMLImageElement as imageData for backward compatibility
        notifyImageStatusChange(message.id, LiveImageStatus.LOADED, img);
      };
      img.onerror = () => {
        message.imageWithStatus.loadingStatus = LiveImageStatus.FAILED;
        notifyImageStatusChange(message.id, LiveImageStatus.FAILED);
      };
      img.src = processedImageUrl;
    } else {
      // It's an S3 key, start async Cloudinary loading
      loadCloudinaryImageForMessage(message, processedImageUrl);
    }
  }
  
  return message;
}
